/** Tier-1 confluence bonus — one winner per ticker (api/scan.js + App.jsx). */

import { CONFLUENCE_TIPS } from "./signalTooltips.js";

const PROX = 0.015;

function nearLevel(price, level) {
  if (price == null || level == null || level <= 0) return false;
  return Math.abs(price - level) / level <= PROX;
}

function emaLatest(closes, period) {
  if (!closes?.length || closes.length < period) return null;
  const chrono = [...closes].reverse();
  let ema = chrono.slice(0, period).reduce((s, v) => s + v, 0) / period;
  const k = 2 / (period + 1);
  for (let i = period; i < chrono.length; i++) {
    ema = chrono[i] * k + ema * (1 - k);
  }
  return ema;
}

function calcCMF(h, l, c, v, n = 20) {
  if (!c?.length || c.length < n) return null;
  let mfv = 0;
  let vol = 0;
  for (let i = 0; i < n; i++) {
    const hl = h[i] - l[i];
    if (!hl) continue;
    mfv += ((c[i] - l[i]) - (h[i] - c[i])) / hl * v[i];
    vol += v[i];
  }
  return vol ? mfv / vol : 0;
}

function calcObvMetrics(closes, volumes) {
  if (!closes?.length || !volumes?.length || closes.length < 16) return null;
  let obv = 0;
  const obvArr = [0];
  for (let i = 0; i < closes.length - 1; i++) {
    if (closes[i] > closes[i + 1]) obv += volumes[i] || 0;
    else if (closes[i] < closes[i + 1]) obv -= volumes[i] || 0;
    obvArr.push(obv);
  }
  const recentOBV = obvArr.slice(0, 5).reduce((s, v) => s + v, 0) / Math.min(5, obvArr.length);
  const priorOBV = obvArr.slice(5, 15).reduce((s, v) => s + v, 0) / Math.min(10, obvArr.length - 5);
  const priceRising = closes[0] > closes[5];
  const obvRising = recentOBV > priorOBV;
  const obvFlatOrRising = recentOBV >= priorOBV * 0.98;
  return {
    rising: obvRising,
    flatOrRising: obvFlatOrRising,
    bullishAccum: !priceRising && obvRising,
  };
}

function avgVol(volumes, start, len) {
  const slice = volumes.slice(start, start + len).filter((v) => v != null && v > 0);
  if (!slice.length) return null;
  return slice.reduce((s, v) => s + v, 0) / slice.length;
}

function donchianBreak20(highs, closes) {
  if (!highs?.length || highs.length < 21 || !closes?.length) return { upper: null, breaking: false };
  const upper = Math.max(...highs.slice(1, 21));
  const breaking = closes[0] > upper;
  return { upper, breaking };
}

function buildSetup(def) {
  const allPass = def.conditions.every((c) => c.pass);
  return {
    ...def,
    allPass,
    magnitude: Math.abs(def.bonus),
  };
}

/**
 * @returns {{ type: string|null, bonus: number, setupType: string|null, reason: string|null, reasons: string[], conditions: object[], label: string|null }}
 */
export function evaluateConfluence(input) {
  const empty = {
    type: null,
    bonus: 0,
    setupType: null,
    reason: null,
    reasons: [],
    conditions: [],
    label: null,
  };

  const { closes, highs, lows, volumes, price: priceIn, fibSwing, fibBounce, rsiDiv } = input || {};
  if (!closes?.length || !highs?.length || !lows?.length || !volumes?.length) return empty;

  const price = priceIn ?? closes[0];
  const cmf = calcCMF(highs, lows, closes, volumes);
  const obv = calcObvMetrics(closes, volumes);
  const ema50 = emaLatest(closes, 50);
  const ema200 = emaLatest(closes, 200);
  const structureIntact =
    price != null && ema50 != null && ema200 != null && price > ema50 && ema50 > ema200;

  const fib500 = fibSwing?.fib500;
  const fib618 = fibSwing?.fib618;
  const fib382 = fibSwing?.fib382;
  const nearFib505618 = nearLevel(price, fib500) || nearLevel(price, fib618);

  const vol5 = avgVol(volumes, 0, 5);
  const vol20 = avgVol(volumes, 0, 20);
  const volContracting = vol5 != null && vol20 != null && vol5 < vol20;

  const { upper: donchUpper, breaking: donchianBreak } = donchianBreak20(highs, closes);
  const volConfirming = vol20 != null && volumes[0] != null && volumes[0] > vol20 * 1.5;

  const rsiBull = rsiDiv?.rsiBullishDivergence === true || rsiDiv?.bullish === true;
  const rsiBear = rsiDiv?.rsiBearishDivergence === true || rsiDiv?.bearish === true;

  const nearFib382FromBelow = (() => {
    if (!nearLevel(price, fib382) || fib382 == null) return false;
    const recentLow = Math.min(...lows.slice(0, 10));
    return recentLow < fib382 * 0.99 && price >= fib382 * (1 - PROX);
  })();

  const setups = [
    buildSetup({
      setupType: "FIB_CONFLUENCE_LONG",
      label: "HIGH CONVICTION LONG",
      bonus: 3,
      reason: "Fib pullback with structure + accumulation",
      conditions: [
        { key: "nearFib505618", label: "Price within 1.5% of Fib 50% or 61.8%", pass: nearFib505618 },
        { key: "structure", label: "Price > EMA50 > EMA200", pass: structureIntact },
        { key: "cmf", label: "CMF > 0.10 (accumulation)", pass: cmf != null && cmf > 0.1 },
        { key: "volContract", label: "Volume contracting vs 20d avg (pullback)", pass: volContracting },
        {
          key: "obv",
          label: "OBV slope flat or rising",
          pass: obv?.flatOrRising === true,
        },
      ],
    }),
    buildSetup({
      setupType: "DONCHIAN_BREAKOUT_CONFIRMED",
      label: "BREAKOUT CONFLUENCE",
      bonus: 3,
      reason: "Volume-confirmed Donchian breakout",
      conditions: [
        {
          key: "donchian",
          label: "Break above Donchian 20 upper channel",
          pass: donchianBreak,
          detail: donchUpper != null ? `Upper $${donchUpper.toFixed(2)}` : null,
        },
        { key: "structure", label: "Price > EMA50 > EMA200", pass: structureIntact },
        { key: "volume", label: "Volume > 1.5× 20d average", pass: volConfirming },
        { key: "cmf", label: "CMF > 0.05", pass: cmf != null && cmf > 0.05 },
      ],
    }),
    buildSetup({
      setupType: "FIB_BOUNCE_DIVERGENCE",
      label: "BOUNCE CONFLUENCE",
      bonus: 2,
      reason: "Fib bounce with RSI + OBV divergence",
      conditions: [
        { key: "fibBounce", label: "Fib bounce (low touched fib, close >1% above)", pass: fibBounce === true },
        { key: "rsiBull", label: "RSI bullish divergence", pass: rsiBull },
        {
          key: "obvAccum",
          label: "OBV rising while price was falling",
          pass: obv?.bullishAccum === true,
        },
      ],
    }),
    buildSetup({
      setupType: "DISTRIBUTION_WARNING",
      label: "DISTRIBUTION WARNING",
      bonus: -2,
      reason: "Weak rally to fib resistance with distribution",
      conditions: [
        {
          key: "fib382Below",
          label: "Rally into Fib 38.2% from below (within 1.5%)",
          pass: nearFib382FromBelow,
        },
        { key: "cmfNeg", label: "CMF < 0", pass: cmf != null && cmf < 0 },
        { key: "rsiBear", label: "RSI bearish divergence", pass: rsiBear },
      ],
    }),
  ];

  const winners = setups.filter((s) => s.allPass);
  if (winners.length) {
    winners.sort((a, b) => {
      if (b.magnitude !== a.magnitude) return b.magnitude - a.magnitude;
      return setups.indexOf(a) - setups.indexOf(b);
    });
    const win = winners[0];
    return {
      type: win.setupType,
      setupType: win.setupType,
      bonus: win.bonus,
      reason: win.reason,
      reasons: [win.reason],
      label: win.label,
      conditions: win.conditions,
      active: true,
    };
  }

  let bestPartial = setups[0];
  let bestCount = -1;
  for (const s of setups) {
    const n = s.conditions.filter((c) => c.pass).length;
    if (n > bestCount) {
      bestCount = n;
      bestPartial = s;
    }
  }

  return {
    ...empty,
    label: bestPartial.label,
    setupType: bestPartial.setupType,
    conditions: bestPartial.conditions,
    active: false,
    partialMatch: bestCount,
  };
}

/** Compact watchlist cell label + tooltip. */
export function watchlistConfluenceCell(result) {
  const c = result?.confluence;
  if (c?.active && c.setupType) {
    switch (c.setupType) {
      case "FIB_CONFLUENCE_LONG":
        return { text: "FIB 🎯", tone: "bull", tip: CONFLUENCE_TIPS["FIB 🎯"] };
      case "DONCHIAN_BREAKOUT_CONFIRMED":
        return { text: "BREAK ⚡", tone: "breakout", tip: CONFLUENCE_TIPS["BREAK ⚡"] };
      case "FIB_BOUNCE_DIVERGENCE":
        return { text: "BOUNCE ↗", tone: "bounce", tip: CONFLUENCE_TIPS["BOUNCE ↗"] };
      case "DISTRIBUTION_WARNING":
        return { text: "DIV ⚠", tone: "warn", tip: CONFLUENCE_TIPS["DIV ⚠"] };
      default:
        break;
    }
  }
  const bearDiv =
    result?.rsiDiv?.bearish === true || result?.rsiBearishDivergence === true;
  if (bearDiv) {
    return { text: "DIV ⚠", tone: "warn", tip: CONFLUENCE_TIPS["DIV ⚠"] };
  }
  return { text: "—", tone: "none", tip: CONFLUENCE_TIPS["—"] };
}

export function attachConfluence(payload) {
  const confluence = evaluateConfluence({
    closes: payload.closes,
    highs: payload.highs,
    lows: payload.lows,
    volumes: payload.volumes,
    price: payload.livePrice ?? payload.closes?.[0],
    fibSwing: payload.fibSwing,
    fibBounce: payload.fibBounce,
    rsiDiv: payload.rsiDiv,
  });
  payload.confluence = confluence;
  return payload;
}
